import { z } from 'zod'
import { hasControlChar, isMailAddress, serverText } from './mailAddress'

/** Field kinds the backend provider catalog declares (`ConsoleProviderConfigurationSchema`). */
export const providerFieldKinds = ['text', 'number', 'bool', 'enum', 'secret'] as const
export const providerFieldSchema = z.object({
  key: z.string().regex(/^[A-Z][A-Za-z0-9]*$/).max(64),
  kind: z.enum(providerFieldKinds),
  required: z.boolean(),
  /** Value the backend codec applies to a never-supplied field; a new draft starts from it. */
  default: z.string().max(320).nullable().optional(),
})
export type ProviderField = z.infer<typeof providerFieldSchema>
/** Stored non-secret value as projected by `GET …/configuration`; secrets are always `null`. */
export const providerSettingValueSchema = z.union([z.string().max(1024), z.number().int(), z.boolean(), z.null()])
export type ProviderSettingValue = z.infer<typeof providerSettingValueSchema>

export type ProviderSettingsDraft = {
  /** Non-secret values: booleans for `bool`, strings for every other kind. */
  values: Record<string, string | boolean>
  /** Newly typed secrets only. An empty string keeps the stored credential; nothing stored is ever echoed. */
  secrets: Record<string, string>
}
export type FieldIssue = 'required' | 'invalid' | 'senderAddress'

// Approved design order (Console v2 provider schema). Keys the backend adds are appended in catalog order.
const displayOrder: Readonly<Record<string, readonly string[]>> = {
  native_smtp: ['Host', 'Port', 'UserName', 'Password', 'From', 'DisplayName', 'UseSSL', 'UseStartTls', 'UseFromAsSender'],
  email_sendgrid: ['ApiKey', 'FromEmail', 'FromName', 'ReplyTo', 'TrackOpens', 'TrackClicks', 'SandboxMode', 'IpPoolName'],
}
/** Closed value sets of `enum` fields; the backend accepts exactly these (case-insensitive). */
export const enumOptions: Readonly<Record<string, readonly string[]>> = { Region: ['US', 'EU'] }
/** Example values from the approved design; placeholders only, never submitted. */
export const fieldPlaceholders: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  native_smtp: { Host: 'smtp.postmarkapp.com', Port: '587', From: 'noreply@fusapp.com', DisplayName: 'Fusapp' },
  email_sendgrid: { ApiKey: 'SG.…' },
  email_brevo: { ApiKey: 'xkeysib-…' },
  email_mailgun: { Domain: 'mg.fusapp.com' },
  native_imap: { Host: 'imap.fusapp.com', Port: '993', Folder: 'INBOX' },
  sms_verimor: { Header: 'FUSAPP' },
}
const emailKeys = new Set(['From', 'FromEmail', 'ReplyTo'])
const hostKeys = new Set(['Host', 'Domain'])
const host = z.string().regex(/^(?=.{1,255}$)[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/)

export function orderedFields(providerId: string | null | undefined, fields: readonly ProviderField[]) {
  const order = displayOrder[providerId ?? ''] ?? []
  const rank = (key: string) => { const index = order.indexOf(key); return index < 0 ? order.length : index }
  return fields.map((field, index) => ({ field, index }))
    .sort((a, b) => rank(a.field.key) - rank(b.field.key) || a.index - b.index).map(item => item.field)
}

export function settingsDraftFrom(fields: readonly ProviderField[], stored: Readonly<Record<string, ProviderSettingValue>> | undefined): ProviderSettingsDraft {
  const draft: ProviderSettingsDraft = { values: {}, secrets: {} }
  for (const field of fields) {
    const value = stored?.[field.key] ?? field.default ?? null
    if (field.kind === 'secret') draft.secrets[field.key] = ''
    else if (field.kind === 'bool') draft.values[field.key] = value === true || value === 'true'
    else draft.values[field.key] = value === null ? '' : String(value)
  }
  return draft
}

/** Request map for `PUT …/configuration`: every non-secret field plus only the secrets that were re-entered. */
export function settingsBody(fields: readonly ProviderField[], draft: ProviderSettingsDraft) {
  const body: Record<string, string | boolean> = {}
  for (const field of fields) {
    if (field.kind === 'secret') { if (draft.secrets[field.key]) body[field.key] = draft.secrets[field.key]! }
    else if (field.kind === 'bool') body[field.key] = draft.values[field.key] === true
    else if (field.kind === 'enum' && !String(draft.values[field.key] ?? '').trim()) continue // stored or catalog default applies
    else body[field.key] = String(draft.values[field.key] ?? '').trim()
  }
  return body
}

/** Client-side mirror of the backend codec rules; the server remains authoritative. */
export function settingsIssues(fields: readonly ProviderField[], draft: ProviderSettingsDraft,
  storedSecrets: readonly string[], credentialReentryRequired: boolean, providerId?: string | null) {
  const issues: Record<string, FieldIssue> = {}
  for (const field of fields) {
    if (field.kind === 'bool') continue
    if (field.kind === 'secret') {
      const typed = draft.secrets[field.key] ?? ''
      const usable = storedSecrets.includes(field.key) && !credentialReentryRequired
      if (typed && !serverText(typed)) issues[field.key] = 'invalid' // white space only: backend ValidText refuses it
      else if (field.required && !typed && !usable) issues[field.key] = 'required'
      else if (typed.length > 1024 || hasControlChar(typed)) issues[field.key] = 'invalid'
      continue
    }
    const value = String(draft.values[field.key] ?? '').trim()
    if (!value) { if (field.required) issues[field.key] = 'required'; continue }
    if (field.kind === 'number' && !(/^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 65535)) issues[field.key] = 'invalid'
    else if (field.kind === 'enum' && !(enumOptions[field.key] ?? []).includes(value.toUpperCase())) issues[field.key] = 'invalid'
    else if (emailKeys.has(field.key) && !isMailAddress(serverText(value))) issues[field.key] = 'invalid'
    else if (hostKeys.has(field.key) && !host.safeParse(value).success) issues[field.key] = 'invalid'
    else if (hasControlChar(value) || value.length > 320) issues[field.key] = 'invalid'
  }
  if (draft.values['UseSSL'] === true && draft.values['UseStartTls'] === true) issues['UseStartTls'] = 'invalid'
  // AppKit SMTP uses the user name as the envelope sender (MAIL FROM) unless From = Sender is on; a connection
  // test does not send MAIL FROM, so a non-address user name would pass the test and fail every delivery.
  const userName = String(draft.values['UserName'] ?? '').trim()
  if (providerId === 'native_smtp' && draft.values['UseFromAsSender'] === false && userName && !issues['UserName'] &&
    !isMailAddress(serverText(userName))) issues['UserName'] = 'senderAddress'
  return issues
}
